import { useState, useEffect, useCallback, useRef } from 'react';
import { useSettingsStore } from '../stores/settingsStore';

export interface UseIdleTimeoutOptions {
  onLogout: () => void;
}

export interface UseIdleTimeoutReturn {
  secondsRemaining: number;
  showWarning: boolean;
  extendSession: () => void;
}

// Activity events to track
const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'click',
  'keydown',
  'keypress',
  'scroll',
  'wheel',
  'touchstart',
  'touchmove',
] as const;

// Shared localStorage key used to coordinate the idle clock across every tab /
// window of the same session (including an installed PWA window). Auth0's session
// lives in localStorage and is shared by all tabs, so a logout in one tab signs
// the user out everywhere. Before this coordination, each tab ran its OWN idle
// timer: an idle background tab would fire onLogout() and kill the session even
// though the user was actively working in another tab (GP-1050). Now activity in
// ANY tab is broadcast here, and every tab resets its timer — the session only
// times out when there has been no activity in any tab for the full period.
const ACTIVITY_STORAGE_KEY = 'phox-session-last-activity';

// Read the most recent activity timestamp broadcast by any tab. Returns 0 when
// unavailable (private mode / storage disabled) so callers fall back to local state.
const readSharedActivity = (): number => {
  try {
    const raw = localStorage.getItem(ACTIVITY_STORAGE_KEY);
    return raw ? Number(raw) || 0 : 0;
  } catch {
    return 0;
  }
};

// Broadcast a fresh activity timestamp to the other tabs. Best-effort — swallow
// storage errors (private mode / quota) rather than break the idle timer.
const writeSharedActivity = (timestamp: number): void => {
  try {
    localStorage.setItem(ACTIVITY_STORAGE_KEY, String(timestamp));
  } catch {
    // ignore — cross-tab coordination degrades gracefully to per-tab timing
  }
};

/**
 * Hook to track user inactivity and manage session timeout
 *
 * Fetches timeout settings from the settings store:
 * - security.session.autoLogoutEnabled: Whether auto-logout is enabled
 * - security.session.inactivityTimeoutMinutes: Time before logout (default 30)
 * - security.session.warningMinutes: Warning time before logout (default 2)
 */
export const useIdleTimeout = ({ onLogout }: UseIdleTimeoutOptions): UseIdleTimeoutReturn => {
  const { settings } = useSettingsStore();

  // Parse settings
  const settingsMap = settings.reduce((acc, setting) => {
    const value = setting.value === 'true' ? true :
                  setting.value === 'false' ? false :
                  setting.value;
    acc[setting.settingKey] = value;
    return acc;
  }, {} as Record<string, any>);

  const enabled = settingsMap['security.session.autoLogoutEnabled'] ?? true;
  const timeoutMinutes = Number(settingsMap['security.session.inactivityTimeoutMinutes']) || 30;
  const warningMinutes = Number(settingsMap['security.session.warningMinutes']) || 2;

  const [showWarning, setShowWarning] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(warningMinutes * 60);

  // Refs to track timers
  const warningTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const showWarningRef = useRef(false);
  const warningStartTimeRef = useRef<number | null>(null);

  // Keep ref in sync for stable callback references
  showWarningRef.current = showWarning;

  // Clear all timers
  const clearTimers = useCallback(() => {
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  // Start the countdown when warning is shown
  const startCountdown = useCallback((initialSeconds?: number) => {
    const warningSeconds = initialSeconds ?? warningMinutes * 60;
    warningStartTimeRef.current = Date.now();
    setSecondsRemaining(warningSeconds);
    setShowWarning(true);

    countdownIntervalRef.current = setInterval(() => {
      setSecondsRemaining(prev => {
        if (prev <= 1) {
          clearTimers();
          onLogout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [warningMinutes, clearTimers, onLogout]);

  // Reset the inactivity timer.
  // `broadcast` = true when this reset was caused by real activity in THIS tab,
  // so the fresh timestamp is shared with the other tabs. Resets triggered by
  // another tab's activity (storage event) pass false to avoid a write loop.
  const resetTimer = useCallback((broadcast = false) => {
    if (!enabled) return;

    clearTimers();
    setShowWarning(false);
    warningStartTimeRef.current = null;
    lastActivityRef.current = Date.now();
    if (broadcast) {
      writeSharedActivity(lastActivityRef.current);
    }

    // Calculate time until warning (timeout - warning period)
    const warningDelay = (timeoutMinutes - warningMinutes) * 60 * 1000;

    warningTimeoutRef.current = setTimeout(() => {
      startCountdown();
    }, warningDelay);
  }, [enabled, timeoutMinutes, warningMinutes, clearTimers, startCountdown]);

  // Handle user activity - only reset if warning is not showing
  const handleActivity = useCallback(() => {
    if (!showWarningRef.current) {
      // Throttle activity handling to avoid excessive timer resets
      const now = Date.now();
      if (now - lastActivityRef.current > 1000) {
        lastActivityRef.current = now;
        resetTimer(true);
      }
    }
  }, [resetTimer]);

  // Handle activity broadcast from another tab of the same session. Real activity
  // elsewhere means the user is NOT idle, so reset even while the warning dialog
  // is showing — otherwise a background tab would still log everyone out (GP-1050).
  const handleStorageActivity = useCallback((event: StorageEvent) => {
    if (!enabled || event.key !== ACTIVITY_STORAGE_KEY || !event.newValue) return;

    const sharedActivity = Number(event.newValue) || 0;
    if (sharedActivity > lastActivityRef.current) {
      lastActivityRef.current = sharedActivity;
      resetTimer(false);
    }
  }, [enabled, resetTimer]);

  // Reconcile timer state when tab becomes visible again
  // Browsers throttle/pause setTimeout/setInterval in background tabs,
  // so we recalculate from wall-clock time on tab return
  const handleVisibilityChange = useCallback(() => {
    if (!enabled || document.visibilityState !== 'visible') return;

    // Adopt the newest cross-tab activity in case a storage event was missed
    // while this tab was hidden (browsers can drop events for discarded tabs).
    const sharedActivity = readSharedActivity();
    if (sharedActivity > lastActivityRef.current) {
      lastActivityRef.current = sharedActivity;
    }

    const now = Date.now();
    const totalTimeoutMs = timeoutMinutes * 60 * 1000;
    const warningMs = warningMinutes * 60 * 1000;
    const warningDelayMs = totalTimeoutMs - warningMs;
    const elapsed = now - lastActivityRef.current;

    if (elapsed >= totalTimeoutMs) {
      clearTimers();
      onLogout();
      return;
    }

    if (elapsed >= warningDelayMs) {
      const remainingSeconds = Math.max(0, Math.ceil((totalTimeoutMs - elapsed) / 1000));
      if (remainingSeconds <= 0) {
        clearTimers();
        onLogout();
        return;
      }
      clearTimers();
      startCountdown(remainingSeconds);
      return;
    }

    // Still in pre-warning phase — reschedule with remaining delay
    if (!showWarningRef.current) {
      clearTimers();
      const remainingDelay = warningDelayMs - elapsed;
      warningTimeoutRef.current = setTimeout(() => {
        startCountdown();
      }, remainingDelay);
    }
  }, [enabled, timeoutMinutes, warningMinutes, clearTimers, startCountdown, onLogout]);

  // Extend session - can be called even when warning is showing.
  // Broadcast so the other tabs (which may also be showing the warning) extend too.
  const extendSession = useCallback(() => {
    resetTimer(true);
  }, [resetTimer]);

  // Set up activity listeners
  useEffect(() => {
    if (!enabled) {
      clearTimers();
      return;
    }

    // Start the initial timer. Opening/loading a tab is itself activity, so
    // broadcast it — this keeps every tab's idle clock aligned to one session.
    resetTimer(true);

    // Add event listeners for activity tracking
    ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });
    document.addEventListener('visibilitychange', handleVisibilityChange);
    // Listen for activity broadcast by the other tabs of this session
    window.addEventListener('storage', handleStorageActivity);

    return () => {
      clearTimers();
      ACTIVITY_EVENTS.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('storage', handleStorageActivity);
    };
  }, [enabled, handleActivity, handleVisibilityChange, handleStorageActivity, resetTimer, clearTimers]);

  // Update seconds remaining when warning minutes changes
  useEffect(() => {
    if (!showWarning) {
      setSecondsRemaining(warningMinutes * 60);
    }
  }, [warningMinutes, showWarning]);

  return {
    secondsRemaining,
    showWarning,
    extendSession,
  };
};
